import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  getConfigureOptions,
  type ConfigureOptionsResponse,
} from "../api/configure-options";
import type { RegionCode } from "../api/filter";
import { toCalculationRateLimitError } from "../utils/calculation-request-control";

export interface UseConfigureOptionsArgs {
  productId: string | null;
  region: RegionCode;
  /**
   * Apps checked for THIS machine (Q1) — drives tooling lookups + auto-select.
   * Snapshotted at modal-open time per Q7; stable for the modal's lifetime.
   */
  applicationIds: string[];
  /** Compare-card MSPe toggle (D9). */
  mspAutoFeedEnabled?: boolean;
  /** Modal-open flag — short-circuits the query when the modal is closed. */
  enabled: boolean;
}

/**
 * Fetches `GET /v1/products/:productId/configure-options`. The query key
 * includes `applicationIds` so refresh-on-assignment-change works (POC
 * `refreshOpenConfigSelectionsAfterAssignmentChange` parity). Region is
 * snapshotted by the caller; this hook treats it as a normal cache key.
 *
 * Stale time = 30s — admin PUTs to cost-models bump version, but the
 * configure surface reads catalog tables that don't share that cache key.
 * The user closing + reopening the modal hard-resets via `enabled=false`.
 */
export function useConfigureOptions(args: UseConfigureOptionsArgs) {
  const { productId, region, applicationIds, mspAutoFeedEnabled, enabled } = args;
  return useQuery<ConfigureOptionsResponse>({
    queryKey: [
      "configure-options",
      productId,
      region,
      [...applicationIds].sort().join(","),
      mspAutoFeedEnabled ?? null,
    ],
    queryFn: ({ signal }) =>
      getConfigureOptions(
        productId as string,
        {
          region,
          applicationIds,
          mspAutoFeedEnabled,
        },
        signal,
      ),
    enabled: enabled && !!productId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: (failureCount, error) =>
      !toCalculationRateLimitError(error) && failureCount < 1,
  });
}
